import { getRawInputKind } from "./cell-kind-utils";
import { normalizeOptimisticCellValue } from "./spreadsheet-input-normalizer";
import {
  AI_SPREADSHEET_CELL_KIND,
  AI_SPREADSHEET_DEFAULT_FORMAT_CODE,
  AI_SPREADSHEET_VALUE_TYPE,
  type AiSpreadsheetCellKind,
  type AiSpreadsheetValueType,
  type IAiSpreadsheetManualRow,
  type IAiSpreadsheetPasteEdit,
  type IAiSpreadsheetCell,
  type IAiSpreadsheetSheetResponse,
  type IManualRowInput,
  type IManualRowsResponse,
  type IUpdateAiSpreadsheetSheetResponse,
} from "./types";

// A sheet's cells live solely in its per-sheet query cache. These helpers
// read and patch that single source of truth — the aggregate workbook query
// only owns sheet metadata (the tab list) and never carries cells.

export const patchSpreadsheetSheetCells = (
  data: IAiSpreadsheetSheetResponse | undefined,
  cells: IAiSpreadsheetCell[],
) => {
  if (!data) return data;

  const cellsById = new Map(cells.map((cell) => [cell.id, cell]));

  return {
    ...data,
    sheet: {
      ...data.sheet,
      cells: data.sheet.cells.map((cell) => cellsById.get(cell.id) ?? cell),
    },
  };
};

export const findSheetResponseCell = (
  data: IAiSpreadsheetSheetResponse | undefined,
  cellId: string,
): IAiSpreadsheetCell | undefined =>
  data?.sheet.cells.find((cell) => cell.id === cellId);

// A fingerprint of the cell state a paste optimistically writes — value, type,
// and display format. Rollback compares this (not just raw_input) so a same-cell
// change of ANY of them (e.g. a "Format cell" that leaves raw_input untouched)
// counts as "the user touched it" and is preserved.
const pasteOptimisticFingerprint = (cell: IAiSpreadsheetCell): string =>
  [
    cell.raw_input ?? "",
    cell.kind ?? "",
    cell.value_type ?? "",
    cell.format?.format_code ?? "",
  ].join(" ");

// Roll back a failed optimistic paste: restore each patched cell's pre-paste
// state, but ONLY where the cell still matches this paste's optimistic
// fingerprint. If the user re-edited or reformatted one of those cells while the
// (non-blocking) paste was in flight, their newer state is left untouched.
export const rollbackOptimisticPasteCells = (
  data: IAiSpreadsheetSheetResponse | undefined,
  rollbacks: { prior: IAiSpreadsheetCell; optimistic: IAiSpreadsheetCell }[],
) => {
  if (!data) return data;

  const byId = new Map(
    rollbacks.map((rollback) => [rollback.prior.id, rollback]),
  );

  return {
    ...data,
    sheet: {
      ...data.sheet,
      cells: data.sheet.cells.map((cell) => {
        const rollback = byId.get(cell.id);
        return rollback &&
          pasteOptimisticFingerprint(cell) ===
            pasteOptimisticFingerprint(rollback.optimistic)
          ? rollback.prior
          : cell;
      }),
    },
  };
};

export const buildOptimisticSpreadsheetCells = (
  cells: IAiSpreadsheetCell[],
  edits: IAiSpreadsheetPasteEdit[],
): IAiSpreadsheetCell[] => {
  const cellsById = new Map(cells.map((cell) => [cell.id, cell]));

  return edits.flatMap((edit) => {
    const cell = cellsById.get(edit.cell_id);
    if (!cell) return [];

    // A paste always writes a NEW value, so paint the value — never the
    // format-only branch of buildOptimisticCell (that's for the "Format cell"
    // menu and would keep the old value, so a numeric paste would show nothing).
    // When the clipboard typed the cell numeric, retype + reformat it FIRST so
    // the value evaluates and renders as that type, then apply the pasted value.
    if (!edit.value_type) {
      return [buildOptimisticCell(cell, edit.raw_input, undefined, edit.kind)];
    }

    const formatCode = AI_SPREADSHEET_DEFAULT_FORMAT_CODE[edit.value_type];
    const nextFormat = { ...(cell.format ?? {}) };
    if (formatCode) nextFormat.format_code = formatCode;
    else delete nextFormat.format_code;
    const typedCell = {
      ...cell,
      value_type: edit.value_type,
      format: nextFormat,
    };

    return [
      buildOptimisticCell(typedCell, edit.raw_input, undefined, edit.kind),
    ];
  });
};

// Build the cell we optimistically render before the server reconciles. We
// can evaluate plain values locally, but formulas (and any cells that depend
// on this edit) still need the server, so we keep their prior computed value.
export const buildOptimisticCell = (
  cell: IAiSpreadsheetCell,
  rawInput: string,
  valueType?: AiSpreadsheetValueType | null,
  kind?: AiSpreadsheetCellKind,
): IAiSpreadsheetCell => {
  // A format-only change: apply the display format + type instantly and keep the
  // value (Excel shifts the display, not the value). The display and the format
  // indicator both key off format_code, so setting it here reformats the cell
  // without waiting for the server. Reconciled by the mutation's onSuccess.
  if (valueType) {
    const formatCode = AI_SPREADSHEET_DEFAULT_FORMAT_CODE[valueType];
    const nextFormat = { ...(cell.format ?? {}) };
    if (formatCode) nextFormat.format_code = formatCode;
    else delete nextFormat.format_code;

    return { ...cell, value_type: valueType, format: nextFormat };
  }

  const nextKind = kind ?? getRawInputKind(rawInput);
  const isFormula = nextKind === AI_SPREADSHEET_CELL_KIND.formula;

  return {
    ...cell,
    raw_input: rawInput,
    kind: nextKind,
    computed_value: isFormula
      ? cell.computed_value
      : normalizeOptimisticCellValue(cell, rawInput),
    error: null,
  };
};

// Drop a manual row's cells from a sheet (optimistic delete). The follow-up
// re-fetch reconciles the rows that shift up to close the gap and the totals
// that recompute server-side.
export const removeManualRowCells = (
  data: IAiSpreadsheetSheetResponse | undefined,
  manualRowId: string,
): IAiSpreadsheetSheetResponse | undefined => {
  if (!data) return data;

  return {
    ...data,
    sheet: {
      ...data.sheet,
      cells: data.sheet.cells.filter(
        (cell) => cell.manual_row_id !== manualRowId,
      ),
    },
  };
};

// Merge server cells into a sheet, appending any that are new (used when a
// manual row is added — patchSpreadsheetSheetCells only replaces by id).
export const addSpreadsheetSheetCells = (
  data: IAiSpreadsheetSheetResponse | undefined,
  cells: IAiSpreadsheetCell[],
) => {
  if (!data) return data;

  const incomingById = new Map(cells.map((cell) => [cell.id, cell]));
  const existingIds = new Set(data.sheet.cells.map((cell) => cell.id));
  const merged = data.sheet.cells.map(
    (cell) => incomingById.get(cell.id) ?? cell,
  );
  const added = cells.filter((cell) => !existingIds.has(cell.id));

  return { ...data, sheet: { ...data.sheet, cells: [...merged, ...added] } };
};

// Prefix of the synthetic ids on optimistic (pre-server) manual-row cells. The
// adapter keeps cells with this prefix read-only until the real cells arrive.
export const OPTIMISTIC_MANUAL_ROW_ID_PREFIX = "optimistic-mr-";

const isGeneratedTextCellWithSuggestions = (
  cell: IAiSpreadsheetCell,
  col: number,
): cell is IAiSpreadsheetCell & { suggested_values: string[] } =>
  cell.col === col &&
  cell.kind === AI_SPREADSHEET_CELL_KIND.value &&
  cell.value_type === AI_SPREADSHEET_VALUE_TYPE.text &&
  cell.suggested_values != null &&
  cell.suggested_values.length > 0 &&
  cell.manual_row_id == null &&
  cell.source_ref?.manual_row_id == null;

// Mirror the API's manual-row inference: only script-generated text value cells
// participate, and the column qualifies only when every supplied ordered list
// is identical. Manual rows cannot teach later optimistic rows stale options.
export const inferColumnSuggestedValues = (
  cells: IAiSpreadsheetCell[],
  col: number,
): string[] | null => {
  const candidates = cells
    .filter((cell) => isGeneratedTextCellWithSuggestions(cell, col))
    .map((cell) => cell.suggested_values);
  const firstCandidate = candidates[0];
  if (!firstCandidate) return null;

  const isConsistent = candidates.every(
    (suggestions) =>
      suggestions.length === firstCandidate.length &&
      suggestions.every(
        (suggestion, index) => suggestion === firstCandidate[index],
      ),
  );
  return isConsistent ? [...firstCandidate] : null;
};

// Build temporary cells for rows being added, so an add shows instantly (before
// the server round-trip). Replaced by the authoritative cells on the follow-up
// sheet re-fetch. `rowInputs[i].cells` is keyed by column number. `token`
// scopes the ids to one mutation (`${PREFIX}${token}-${row}-${col}`) so a
// concurrent add's onSuccess strips only its own optimistic cells — the shared
// prefix stays at the front so `startsWith(PREFIX)` checks still match.
export const buildOptimisticManualCells = (
  sheetId: string,
  startRow: number,
  rowInputs: IManualRowInput[],
  token: string,
  sourceCells: IAiSpreadsheetCell[] = [],
  editableWhilePending = false,
): IAiSpreadsheetCell[] => {
  const cells: IAiSpreadsheetCell[] = [];
  const columnSuggestions = new Map<number, string[] | null>();
  const suggestionsForColumn = (col: number) => {
    if (!columnSuggestions.has(col)) {
      columnSuggestions.set(col, inferColumnSuggestedValues(sourceCells, col));
    }
    return columnSuggestions.get(col) ?? null;
  };

  rowInputs.forEach((rowInput, index) => {
    const row = startRow + index;
    const manualRowId = `${OPTIMISTIC_MANUAL_ROW_ID_PREFIX}${token}-${row}`;
    Object.entries(rowInput.cells ?? {}).forEach(([col, spec]) => {
      const isFormula = spec.kind === AI_SPREADSHEET_CELL_KIND.formula;
      const kind = isFormula
        ? AI_SPREADSHEET_CELL_KIND.formula
        : AI_SPREADSHEET_CELL_KIND.value;
      const valueType = spec.value_type ?? AI_SPREADSHEET_VALUE_TYPE.text;
      const rawInput = isFormula ? spec.raw_input : String(spec.value ?? "");
      const computedValue = isFormula ? null : (spec.value ?? "");
      const columnNumber = Number(col);
      cells.push({
        id: `${manualRowId}-${col}`,
        ai_spreadsheet_sheet_id: sheetId,
        row,
        col: columnNumber,
        kind,
        raw_input: rawInput,
        computed_value: computedValue,
        value_type: valueType,
        format: null,
        error: null,
        accessor_key: null,
        source_ref: {
          manual_row_id: manualRowId,
          ...(editableWhilePending ? { editable_while_pending: true } : {}),
        },
        suggested_values:
          !isFormula && valueType === AI_SPREADSHEET_VALUE_TYPE.text
            ? suggestionsForColumn(columnNumber)
            : null,
        manual_row_id: manualRowId,
        edited_by_admin_user_id: null,
        edited_at: null,
        created_at: "",
        updated_at: "",
      });
    });
  });
  return cells;
};

export const addOptimisticManualRows = (
  data: IAiSpreadsheetSheetResponse | undefined,
  {
    sheetId,
    rowInputs,
    token,
    afterRow,
    editableWhilePending = false,
  }: {
    sheetId: string;
    rowInputs: IManualRowInput[];
    token: string;
    afterRow: number | null;
    editableWhilePending?: boolean;
  },
) => {
  if (!data) return data;

  const startRow =
    afterRow != null
      ? afterRow + 1
      : data.sheet.cells.reduce((max, cell) => Math.max(max, cell.row), 0) + 1;
  const shiftedData =
    afterRow == null
      ? data
      : {
          ...data,
          sheet: {
            ...data.sheet,
            cells: data.sheet.cells.map((cell) =>
              cell.row > afterRow ? { ...cell, row: cell.row + 1 } : cell,
            ),
          },
        };

  return addSpreadsheetSheetCells(
    shiftedData,
    buildOptimisticManualCells(
      sheetId,
      startRow,
      rowInputs,
      token,
      data.sheet.cells,
      editableWhilePending,
    ),
  );
};

export const reconcileCreatedManualRowCells = (
  data: IAiSpreadsheetSheetResponse | undefined,
  cells: IAiSpreadsheetCell[],
  optimisticToken?: string,
) => {
  if (!data) return data;

  const optimisticPrefix = optimisticToken
    ? `${OPTIMISTIC_MANUAL_ROW_ID_PREFIX}${optimisticToken}-`
    : null;
  const reconciledData = optimisticPrefix
    ? {
        ...data,
        sheet: {
          ...data.sheet,
          cells: data.sheet.cells.filter(
            (cell) => !cell.id.startsWith(optimisticPrefix),
          ),
        },
      }
    : data;

  return addSpreadsheetSheetCells(reconciledData, cells);
};

export const reconcileBulkPasteSheetCells = (
  data: IAiSpreadsheetSheetResponse | undefined,
  cells: IAiSpreadsheetCell[],
  optimisticToken?: string,
) =>
  reconcileCreatedManualRowCells(
    data,
    cells.filter((cell) => cell.ai_spreadsheet_sheet_id === data?.sheet.id),
    optimisticToken,
  );

export const mergeManualRows = (
  data: IManualRowsResponse | undefined,
  incomingRows: IAiSpreadsheetManualRow[],
): IManualRowsResponse | undefined => {
  if (!data) return data;

  const incomingById = new Map(incomingRows.map((row) => [row.id, row]));
  const existingIds = new Set(data.manual_rows.map((row) => row.id));

  return {
    manual_rows: [
      ...data.manual_rows.map((row) => incomingById.get(row.id) ?? row),
      ...incomingRows.filter((row) => !existingIds.has(row.id)),
    ],
  };
};

export const patchSpreadsheetSheetLayout = (
  data: IAiSpreadsheetSheetResponse | undefined,
  sheet: IUpdateAiSpreadsheetSheetResponse["sheet"],
) => {
  if (!data || data.sheet.id !== sheet.id) return data;

  return {
    ...data,
    sheet: {
      ...data.sheet,
      column_widths: sheet.column_widths,
      row_heights: sheet.row_heights,
      frozen_column: sheet.frozen_column,
    },
  };
};
